import { useContext } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import { Body, Cell, Row, Table } from '@/design_system/Table/Table';
import Tooltip from '@/design_system/Tooltip';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconInfo from '@/icons/Info.svg';
import IconTools from '@/icons/Tools.svg';
import { useActionTypesOrganization } from '@/models/actionType';
import { AgreementFromGetById } from '@/models/agreements';
import { useDatabaseTranslation } from '@/services/i18n';

import { AgreementEditContext } from '../AgreementEditContext';

import {
  getPriceAgreementColumnWidths,
  PriceAgreementTableHeader,
  PriceAgreementTableRow,
  PriceAgreementTableRowEdit,
  PriceAgreementTableRowNew,
} from './PriceAgreementTableRow';

export const PackActionsTable = ({
  packActions,
  agreement,
}: {
  packActions: AgreementFromGetById['prices'];
  agreement: AgreementFromGetById;
}) => {
  const { _compare } = useDatabaseTranslation();
  const { draft } = useContext(AgreementEditContext);

  const withMargin = agreement.clientB;
  const withWorkshopWarrantyDurationInMonths = !agreement.clientB;

  const sorted = packActions.sort((a, b) =>
    _compare(a.packActionTypeOrganization!.name!, b.packActionTypeOrganization!.name!)
  );

  if (draft) {
    return (
      <PackActionsTableEdit
        packActions={packActions}
        withMargin={withMargin}
        withWorkshopWarrantyDurationInMonths={withWorkshopWarrantyDurationInMonths}
      />
    );
  }

  return (
    <Table
      columnWidths={getPriceAgreementColumnWidths({
        withMargin,
        withWorkshopWarrantyDurationInMonths,
      })}
      bordered
    >
      <PriceAgreementTableHeader
        withMargin={withMargin}
        withWorkshopWarrantyDurationInMonths={withWorkshopWarrantyDurationInMonths}
      />
      <Body>
        {sorted.map((price) => (
          <PriceAgreementTableRow
            key={price.id}
            price={price}
            withMargin={withMargin}
            withWorkshopWarrantyDurationInMonths={withWorkshopWarrantyDurationInMonths}
          >
            <PackActionsNameCell price={price} />
          </PriceAgreementTableRow>
        ))}
        {sorted.length === 0 && (
          <Row>
            <Cell isWholeRow>
              <Paragraph color="secondary">
                <Trans id="settings.organization.entities.agreements.detail.empty.pack-actions">
                  No pack of actions prices on this agreement.
                </Trans>
              </Paragraph>
            </Cell>
          </Row>
        )}
      </Body>
    </Table>
  );
};

const PackActionsTableEdit = ({
  packActions,
  withMargin,
  withWorkshopWarrantyDurationInMonths,
}: {
  packActions: AgreementFromGetById['prices'];
  withMargin: boolean;
  withWorkshopWarrantyDurationInMonths: boolean;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { draft } = useContext(AgreementEditContext);

  const { data: { packActionTypes = [] } = {} } = useActionTypesOrganization();

  const missingPackActionTypes = packActionTypes.filter(
    (packActionType) =>
      !draft.prices.some((price) => price.packActionTypeOrganizationId === packActionType.id)
  );

  return (
    <Table
      columnWidths={getPriceAgreementColumnWidths({
        withMargin,
        withWorkshopWarrantyDurationInMonths,
        withActions: true,
      })}
      bordered
    >
      <PriceAgreementTableHeader
        withActions
        withMargin={withMargin}
        withWorkshopWarrantyDurationInMonths={withWorkshopWarrantyDurationInMonths}
      />
      <Body>
        {packActions.map((price) => (
          <PriceAgreementTableRowEdit
            key={price.id}
            price={price}
            withMargin={withMargin}
            withWorkshopWarrantyDurationInMonths={withWorkshopWarrantyDurationInMonths}
          >
            <PackActionsNameCell price={price} />
          </PriceAgreementTableRowEdit>
        ))}
        <PriceAgreementTableRowNew
          placeholder={t({
            id: 'settings.organization.entities.agreements.detail.table.pack-actions.add',
            message: 'Add pack of actions',
          })}
          options={missingPackActionTypes.map((packActionType) => ({
            label: _db(packActionType.name),
            ...packActionType,
          }))}
          onSelect={(value) => ({
            packActionTypeOrganization: value,
            packActionTypeOrganizationId: value.id,
          })}
          withMargin={withMargin}
          withWorkshopWarrantyDurationInMonths={withWorkshopWarrantyDurationInMonths}
        />
      </Body>
    </Table>
  );
};

const PackActionsNameCell = ({ price }: { price: AgreementFromGetById['prices'][number] }) => {
  const { _db } = useDatabaseTranslation();

  return (
    <Stack row gap="0.35rem" alignItems="center" flexWrap="nowrap">
      <IconTools style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
      <Paragraph>{_db(price.packActionTypeOrganization!.name!)}</Paragraph>
      <Tooltip
        size="medium"
        content={
          <Stack alignItems="start">
            {price.packActionTypeOrganization!.actions.map((action) => (
              <Paragraph key={action.id} style={{ textAlign: 'left' }}>
                {action.name && _db(action.name)}
              </Paragraph>
            ))}
          </Stack>
        }
      >
        <Button variant="style-less">
          <IconInfo style={{ fontSize: '1rem', color: 'var(--color-text-disabled-black)' }} />
        </Button>
      </Tooltip>
    </Stack>
  );
};
